import { useState, useEffect, useRef } from 'react';
import FormSetupTutorialStep1 from './FormSetupTutorialStep1';
import FormSetupTutorialStep2 from './FormSetupTutorialStep2';
import FormSetupTutorialStep3 from './FormSetupTutorialStep3';
import FormSetupTutorialStep4 from './FormSetupTutorialStep4';
import FormCompletionScreen from '../FormCompletionScreen';
import { useBeforeUnload } from '@/features/agent/hooks/useBeforeUnload';
import useCreateCandidateForm from '../../api/useCreateCandidateForm';

interface Props {
  isCreating?: boolean;
  onFormCreated?: () => void;
}

export default function FormSetupTutorial({
  isCreating: externalIsCreating = false,
  onFormCreated,
}: Props) {
  const createFormMutation = useCreateCandidateForm();
  const [isCreating, setIsCreating] = useState(false);
  const [isCompleted, setIsCompleted] = useState(false);
  const [createdFormUrl, setCreatedFormUrl] = useState<string | undefined>(undefined);
  const [currentStep, setCurrentStep] = useState<1 | 2 | 3 | 4>(1);
  // ステップ2の入力内容: 選択されたカスタムフィールドID
  const [selectedFieldIds, setSelectedFieldIds] = useState<Set<number>>(new Set());
  // ステップ3の入力内容: タイトルとロゴファイル
  const [title, setTitle] = useState('エントリーフォーム');
  const [logoFile, setLogoFile] = useState<File | null>(null);
  // ステップ4の入力内容: サンクスページのタイトルとメッセージ
  const [thanksPageTitle, setThanksPageTitle] = useState('送信完了ポスト');
  const [thanksPageMessage, setThanksPageMessage] = useState('フォームの送信を完了致しました。');
  const [isNavigating, setIsNavigating] = useState(false);
  const isNavigatingRef = useRef(false);

  // 入力中かどうかを判定（デフォルト値から変更があった場合）
  const hasUnsavedChanges =
    !isNavigating &&
    (selectedFieldIds.size > 0 ||
      title !== 'エントリーフォーム' ||
      logoFile !== null ||
      thanksPageTitle !== '送信完了ポスト' ||
      thanksPageMessage !== 'フォームの送信を完了致しました。');

  // ページを離れようとしたときの警告（ブラウザのタブを閉じる、リロードなど）
  useBeforeUnload(hasUnsavedChanges);

  // リンククリック時の確認（グローバルなイベントリスナーとして設定）
  useEffect(() => {
    if (!hasUnsavedChanges) return;

    const handleClick = (e: MouseEvent) => {
      if (isNavigatingRef.current) {
        isNavigatingRef.current = false;
        return;
      }

      const target = e.target as HTMLElement;
      const link = target.closest('a[href]') as HTMLAnchorElement | null;

      if (link && link.href && !link.href.startsWith('#')) {
        // 同じオリジンのリンクかチェック
        const url = new URL(link.href);
        if (url.origin === window.location.origin && url.pathname !== window.location.pathname) {
          if (
            !window.confirm('入力中の内容が失われる可能性があります。本当にページを離れますか？')
          ) {
            e.preventDefault();
            e.stopPropagation();
            e.stopImmediatePropagation();
            return false;
          }
          isNavigatingRef.current = true;
        }
      }
    };

    document.addEventListener('click', handleClick, true);

    return () => {
      document.removeEventListener('click', handleClick, true);
    };
  }, [hasUnsavedChanges]);

  const handleNext = () => {
    if (currentStep < 4) {
      setCurrentStep((prev) => (prev + 1) as 1 | 2 | 3 | 4);
    }
  };

  const handleBack = () => {
    if (currentStep > 1) {
      setCurrentStep((prev) => (prev - 1) as 1 | 2 | 3 | 4);
    }
  };

  const handleCreateForm = async () => {
    try {
      setIsCreating(true);
      setIsNavigating(true); // 遷移前に警告を解除
      await createFormMutation.mutateAsync({
        title: title,
        color_type: 'white',
        custom_field_ids: Array.from(selectedFieldIds),
      });
      // 作成成功後、親コンポーネントに通知
      if (onFormCreated) {
        onFormCreated();
      } else {
        // フォールバック: 親コンポーネントがコールバックを提供していない場合
        // TODO: フォームのURLを取得して設定する（必要に応じてAPIレスポンスにurlが含まれる場合は設定）
        setCreatedFormUrl(undefined);
        setIsCompleted(true);
      }
    } catch (error) {
      console.error('Failed to create form:', error);
      // エラーハンドリングは必要に応じて追加
      setIsCreating(false);
      setIsNavigating(false); // エラー時は警告を再有効化
    }
  };

  // 完了画面を表示（親コンポーネントがコールバックを提供していない場合のみ）
  if (isCompleted && !onFormCreated) {
    return <FormCompletionScreen formUrl={createdFormUrl} />;
  }

  return (
    <div className="flex min-h-[calc(100vh-120px)] items-center justify-center p-6">
      <div className="w-full max-w-[1000px] rounded-md bg-white shadow-[0px_0px_12px_0px_rgba(0,0,0,0.2)]">
        {currentStep === 1 && <FormSetupTutorialStep1 onNext={handleNext} />}
        {currentStep === 2 && (
          <FormSetupTutorialStep2
            onNext={handleNext}
            onBack={handleBack}
            selectedFieldIds={selectedFieldIds}
            onSelectedFieldIdsChange={setSelectedFieldIds}
          />
        )}
        {currentStep === 3 && (
          <FormSetupTutorialStep3
            onNext={handleNext}
            onBack={handleBack}
            isCreating={isCreating}
            title={title}
            onTitleChange={setTitle}
            logoFile={logoFile}
            onLogoFileChange={setLogoFile}
          />
        )}
        {currentStep === 4 && (
          <FormSetupTutorialStep4
            onBack={handleBack}
            onCreate={handleCreateForm}
            isCreating={isCreating || externalIsCreating}
            thanksPageTitle={thanksPageTitle}
            onThanksPageTitleChange={setThanksPageTitle}
            thanksPageMessage={thanksPageMessage}
            onThanksPageMessageChange={setThanksPageMessage}
          />
        )}
      </div>
    </div>
  );
}
